import type { BrandPaymentConfig } from '../types'
import { chapterCommonTexts, payCommonTexts, subscribeCommonTexts } from '../common'
import { colors as brandColors, theme as brandTheme } from '@/config/brands'

function hexToRgba(color: string, alpha: number) {
  if (!color.startsWith('#'))
    return color

  const hex = color.slice(1)
  const normalizedHex = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16)
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16)
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const primaryGradientStart = '#A66BFF'
const primaryGradientEnd = '#8E47FF'
const primaryGradient = `linear-gradient(90deg, ${primaryGradientStart} 0%, ${primaryGradientEnd} 100%)`
const paymentStickyBgStart = brandTheme.bgGlobal || '#261936'
const paymentStickyGradient = `linear-gradient(to top, ${paymentStickyBgStart} 0%, ${paymentStickyBgStart} 34%, ${hexToRgba(paymentStickyBgStart, 0.92)} 58%, ${hexToRgba(paymentStickyBgStart, 0.72)} 74%, ${hexToRgba(paymentStickyBgStart, 0.36)} 88%, ${hexToRgba(paymentStickyBgStart, 0)} 100%)`

export const novarisPaymentConfig: BrandPaymentConfig = {
  brand: 'novaris',

  pay: {
    theme: {
      isLightPaymentSelector: false,
      paymentStickyGradient,
      colors: {
        pageBg: '#1A1029',
        cardBg: '#2A1B3D',
        cardBorder: 'rgba(255, 255, 255, 0.08)',
        cardActiveBorder: '#8E47FF',
        cardActiveShadow: 'rgba(142, 71, 255, 0.20)',
        cardRightBg: '#1A1029',
        bonusPillBg: '#35224D',
        bonusPillText: '#C7A7FF',
        bonusRateText: '#A66BFF',
        bottomBarBg: '#2A1B3D',
        bottomBarBorder: 'rgba(255, 255, 255, 0.08)',
        totalHighlight: '#8E47FF',
        payBtnGradient: 'linear-gradient(90deg, #A66BFF 0%, #8E47FF 100%)',
        payBtnShadow: 'rgba(142, 71, 255, 0.30)',
        tagGradient: 'linear-gradient(132deg, #F961BC 0%, #F74984 100%)',
      },
    },
    texts: payCommonTexts,
    resources: {
      goldCoinIcon: '/images/novaris/welfare-gold.png',
    },
  },

  subscribe: {
    texts: subscribeCommonTexts,
    darkPaymentSelector: true,
    resources: {
      vipBg: '/images/novaris/vip_bg.webp',
      memberSubscribeBg: '/images/novaris/member_subscribe_bg.webp',
      vipIcon: '/images/novaris/vip_icon2.png',
      vipDiscountIcon: '/images/novaris/vip_discount.png',
      discountBg: '/images/novaris/discount.avif',
      showVipIcon: true,
    },
    colors: {
      redGradient: primaryGradient,
      purpleTextGradient: 'linear-gradient(180deg, #D8C0FF 0%, #A66BFF 100%)',
      subscribeText1: brandColors!.primary,
      subscribeText2: brandColors!.text.primary,
      subscribeText3: brandColors!.text.secondary,
      subscribeText4: brandColors!.text.secondary,
      subscribeCardBg: 'linear-gradient(90deg, #35224D 0%, #2F1F43 100%)',
      cycleTextGradient: 'linear-gradient(180deg, #D8C0FF 0%, #A66BFF 100%)',
      badgeGradient: 'linear-gradient(132deg, #C9A2FF 0%, #8E47FF 100%)',
      pageBg: '#1A1029',
      paymentCardBg: '#2A1B3D',
      paymentCardBorder: 'rgba(255, 255, 255, 0.08)',
      paymentCardActiveBorder: '#8E47FF',
      tagGradient: 'linear-gradient(132deg, #F961BC 0%, #F74984 100%)',
      titleColor: '#F0EAFA',
      schemes: {
        discounted: {
          bg: 'linear-gradient(270deg, #5E1A42 0%, #3A0E28 100%)',
          priceBg: 'linear-gradient(90deg, #F06292 0%, #C2185B 100%)',
          titleColor: '#F0EAFA',
          descColor: '#F5A8C8',
          borderColor: 'transparent',
          unitPriceColor: '#F5A8C8',
          originalPriceColor: '#F5A8C8',
          cycleLabelBg: 'linear-gradient(90deg, #6E2A52 0%, #5E1A42 100%)',
          cycleLabelColor: '#F0EAFA',
        },
        normal: {
          bg: 'linear-gradient(90deg, #3A1E5C 0%, #2F1F43 100%)',
          priceBg: 'linear-gradient(90deg, #8E47FF 0%, #6A2BFF 100%)',
          titleColor: '#F0EAFA',
          descColor: '#C7A7FF',
          borderColor: 'transparent',
          unitPriceColor: '#C7A7FF',
          originalPriceColor: '#C7A7FF',
          cycleLabelBg: 'linear-gradient(270deg, #3A1E5C 0%, #2F1F43 100%)',
          cycleLabelColor: '#F0EAFA',
        },
        fakeDiscount: {
          bg: 'linear-gradient(270deg, #5E1A42 0%, #3A0E28 100%)',
          priceBg: 'linear-gradient(90deg, #F06292 0%, #C2185B 100%)',
          titleColor: '#F0EAFA',
          descColor: '#F5A8C8',
          borderColor: 'transparent',
          unitPriceColor: '#F5A8C8',
          originalPriceColor: '#F5A8C8',
          cycleLabelBg: 'linear-gradient(270deg, #6E2A52 0%, #5E1A42 100%)',
          cycleLabelColor: '#F0EAFA',
        },
      },
    },
  },

  chapter: {
    texts: chapterCommonTexts,
    darkPaymentSelector: true,
    themeDefaults: {
      background: '#292929',
      size: '38',
    },
    colors: {
      coinsGradient: `linear-gradient(180deg, ${brandColors!.primary}, ${brandColors!.bg.global})`,
      coinsPopupText1: brandColors!.text.inverse,
      coinsPopupText2: primaryGradientStart,
      coinsPopupText3: brandColors!.text.secondary,
      coinsPopupText4: brandColors!.text.inverse,
      vipBg: brandColors!.bg.card,
      subscribeActiveBg: brandColors!.bg.card,
      purpleTextGradient: 'linear-gradient(180deg, #D8C0FF 0%, #A66BFF 100%)',
      pinkGradient: primaryGradient,
      subscribeCardBg: 'linear-gradient(90deg, #35224D 0%, #2F1F43 100%)',
      purchaseActiveBg: '#35224D',
      memberSubscribeBg: '/images/novaris/chapter/member_subscribe_bg.webp',
      showVipIcon: true,
    },
  },
}
